import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Space, Button, Badge, Typography } from 'antd';
import { 
  DashboardOutlined, 
  FileTextOutlined, 
  TeamOutlined,
  AccountBookOutlined, 
  BankOutlined,
  WalletOutlined,
  SettingOutlined,
  CrownOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  StarOutlined,
  LinkOutlined,
  PlusOutlined,
  RobotOutlined,
  QuestionCircleOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import LanguageSwitcher from '../i18n/LanguageSwitcher';
import WalletConnectModal from './WalletConnectModal';
import ImpersonationBanner from './ImpersonationBanner';
import ProjectSwitcher from './ProjectSwitcher';
import { useIsAdmin } from '../admin/impersonation';
import { useNotionMessages } from './notion/useNotionMessages';
import { useProMessages } from '../i18n/pro';
import { trackPageView } from '../api/activity';
import './Layout.css';

const { Header, Content, Sider } = AntLayout;
const { Text } = Typography;

const TELEGRAM_BOT_USERNAME = process.env.REACT_APP_TELEGRAM_BOT_USERNAME?.replace(/^@/, '') || '';
const BOT_URL = TELEGRAM_BOT_USERNAME ? `https://t.me/${TELEGRAM_BOT_USERNAME}` : 'https://t.me/stars_paid_bot';
const HELP_CENTER_URL = 'https://t.me/trictoune';
const HELP_CENTER_HANDLE = '@trictoune';

const Layout: React.FC = () => {
  const { user, logout, firebaseUser, isMiniApp } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const isAdmin = useIsAdmin(firebaseUser?.uid);
  const { adminUsers: ta } = useNotionMessages();
  const proLabels = useProMessages().app;
  const isPro = !!user?.proUntil && user.proUntil.getTime() > Date.now();

  // Journal d'activité (visible dans la session admin) : une entrée par page visitée
  useEffect(() => {
    if (firebaseUser) trackPageView(location.pathname);
  }, [location.pathname, firebaseUser]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
    }
  }, [isMobile]);

  const handleMenuItemClick = (path: string) => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
    navigate(path);
  };

  const menuItems: MenuProps['items'] = useMemo(() => [
    {
      key: '/app/dashboard',
      icon: <DashboardOutlined />,
      label: t.app.layout.dashboard,
      onClick: () => handleMenuItemClick('/app/dashboard'),
    },
    {
      key: '/app/invoices',
      icon: <FileTextOutlined />,
      label: t.app.layout.invoices,
      onClick: () => handleMenuItemClick('/app/invoices'),
    },
    {
      key: '/app/clients',
      icon: <TeamOutlined />,
      label: t.app.layout.clients || 'Clients',
      onClick: () => handleMenuItemClick('/app/clients'),
    },
    {
      key: '/app/ledger',
      icon: <AccountBookOutlined />,
      label: t.app.layout.ledger,
      onClick: () => handleMenuItemClick('/app/ledger'),
    },
    {
      key: '/app/withdraws',
      icon: <BankOutlined />,
      label: t.app.layout.withdraws,
      onClick: () => handleMenuItemClick('/app/withdraws'),
    },
    {
      key: '/app/redirects',
      icon: <LinkOutlined />,
      label: t.app.layout.redirects,
      onClick: () => handleMenuItemClick('/app/redirects'),
    },
    {
      key: '/app/pro',
      icon: <ThunderboltOutlined />,
      label: (
        <span className="menu-pro-label">
          {proLabels.menuLabel}
          <span className={`menu-pro-tag ${isPro ? 'menu-pro-tag--active' : ''}`}>{isPro ? '✓' : '0 %'}</span>
        </span>
      ),
      onClick: () => handleMenuItemClick('/app/pro'),
    },
    ...(isAdmin ? [
      { type: 'divider' as const, className: 'sidebar-admin-divider' },
      {
        key: '/app/admin/users',
        icon: <CrownOutlined />,
        label: ta.menuLabel,
        onClick: () => handleMenuItemClick('/app/admin/users'),
      },
      {
        key: '/app/admin/withdraws',
        icon: <BankOutlined />,
        label: ta.menuWithdraws,
        onClick: () => handleMenuItemClick('/app/admin/withdraws'),
      },
    ] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, ta, isAdmin, proLabels, isPro]);

  const userMenuItems: MenuProps['items'] = useMemo(() => [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t.app.layout.profile,
      onClick: () => navigate('/app/settings'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t.app.layout.settings,
      onClick: () => navigate('/app/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t.app.layout.logout,
      danger: true,
      onClick: async () => {
        await logout();
        navigate('/');
      },
    },
  ], [t, navigate, logout]);

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path.startsWith('/app/invoices')) return ['/app/invoices'];
    if (path.startsWith('/app/withdraws')) return ['/app/withdraws'];
    return [path];
  };

  const handleMenuClick = () => {
    if (isMobile) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  return (
    <AntLayout className={`app-layout ${isMiniApp ? 'app-layout-mini-app' : ''}`} style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={!isMobile && collapsed}
        className={`app-sider vykins-sidebar ${isMobile && !mobileMenuOpen ? 'mobile-hidden' : ''} ${isMobile && mobileMenuOpen ? 'mobile-visible' : ''}`}
        width={260}
      >
        <div className="sidebar-header">
          <div className={`logo-vykins ${!isMobile && collapsed ? 'logo-vykins--collapsed' : ''}`}>
            <img
              src={!isMobile && collapsed ? '/images/logo.png' : '/images/logo_text.png'}
              alt="Telegram Stars"
              className="logo-img-vk"
              onError={(e) => {
                // Image indisponible : on bascule sur le fallback icône + texte
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement?.classList.add('logo-vykins--fallback');
              }}
            />
            <StarOutlined className="logo-icon-fallback-vk" />
            <div className="logo-text-vk">
              <span className="logo-text-telegram">Telegram</span>
              <span className="logo-text-stars">Stars</span>
            </div>
          </div>
          {!isMobile && !collapsed && (
            <Button 
              type="text" 
              icon={<MenuFoldOutlined />}
              className="sidebar-toggle-icon"
              onClick={() => setCollapsed(true)}
            />
          )}
          {isMobile && (
            <Button 
              type="text" 
              icon={<MenuFoldOutlined />}
              className="sidebar-toggle-icon mobile-close-btn"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}
        </div>
        <div className="sidebar-separator"></div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={getSelectedKeys()}
          items={menuItems}
          className="app-menu vykins-menu"
        />
        <div className={`sidebar-footer ${!isMobile && collapsed ? 'sidebar-footer-collapsed' : ''}`}>
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar-footer-link"
            title={TELEGRAM_BOT_USERNAME ? `@${TELEGRAM_BOT_USERNAME}` : t.app.layout.openBot}
          >
            <span className="sidebar-footer-icon"><RobotOutlined /></span>
            <span className="sidebar-footer-text">
              <span className="sidebar-footer-label">{t.app.layout.openBot}</span>
              {TELEGRAM_BOT_USERNAME && <span className="sidebar-footer-sub">@{TELEGRAM_BOT_USERNAME}</span>}
            </span>
          </a>
          <a
            href={HELP_CENTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar-footer-link"
            title={`${t.app.layout.helpCenter} · ${HELP_CENTER_HANDLE}`}
          >
            <span className="sidebar-footer-icon"><QuestionCircleOutlined /></span>
            <span className="sidebar-footer-text">
              <span className="sidebar-footer-label">{t.app.layout.helpCenter}</span>
              <span className="sidebar-footer-sub">{HELP_CENTER_HANDLE}</span>
            </span>
          </a>
        </div>
      </Sider>
      <AntLayout>
        <ImpersonationBanner />
        <Header className="app-header">
          <div className="header-left">
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={handleMenuClick}
                className="mobile-menu-trigger"
              />
            ) : (
              collapsed && (
                <Button
                  type="text"
                  icon={<MenuUnfoldOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                  className="trigger-btn"
                />
              )
            )}
            <ProjectSwitcher />
            {/* Emplacement pour les contrôles de page (ex. sélecteur de période du dashboard, via portail) */}
            <div id="app-header-slot" className="header-slot" />
          </div>
          <div className="header-right">
            {!isMiniApp && <LanguageSwitcher className="layout-lang-switcher" />}
            {isMiniApp && (
              <Button
                type="primary"
                shape="round"
                icon={<PlusOutlined />}
                className="header-new-link-btn"
                onClick={() => navigate('/app/invoices/new')}
                title={t.app.layout.newPaymentLink}
              >
                <span className="header-wallet-label">{t.app.layout.newPaymentLink}</span>
              </Button>
            )}
            {user?.agent && (
              <Badge 
                count={user.agent.tonAddress ? 0 : 1} 
                offset={[-4, 2]}
                title={!user.agent.tonAddress ? t.app.layout.tonNotConfigured : ''}
              >
                <Button 
                  className={`header-wallet-btn ${user.agent.tonAddress ? 'connected' : 'disconnected'}`}
                  icon={user.agent.tonAddress ? <span className="header-wallet-dot" /> : <WalletOutlined />}
                  onClick={() => setWalletModalOpen(true)}
                >
                  <span className="header-wallet-label">
                    {user.agent.tonAddress
                      ? `${user.agent.tonAddress.slice(0, 4)}…${user.agent.tonAddress.slice(-4)}`
                      : t.app.layout.walletConnect}
                  </span>
                </Button>
              </Badge>
            )}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space className="user-dropdown" style={{ cursor: 'pointer' }}>
                <Avatar 
                  icon={<UserOutlined />} 
                  src={firebaseUser?.photoURL}
                  style={{ backgroundColor: '#667eea' }}
                />
                <div className="user-info">
                  <Text strong>{user?.agent?.displayName || firebaseUser?.email || t.app.common.user}</Text>
                  {user?.agent?.tonAddress && (
                    <Text type="secondary" className="user-status">{t.app.layout.active}</Text>
                  )}
                </div>
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content className="app-content">
          <div className="content-container app-page-enter" key={location.pathname}>
            <Outlet />
          </div>
        </Content>
      </AntLayout>
      <WalletConnectModal open={walletModalOpen} onClose={() => setWalletModalOpen(false)} />
    </AntLayout>
  );
};

export default Layout;
